/**
 * VendorLens — shared shell for per-property vendor data layers.
 *
 * Used by PropertyGSC, PropertyGA4, PropertyServerLogs (and any future
 * vendor lens). Provides:
 *   - Header with vendor icon + title + description
 *   - Refresh + Force buttons wired to a vendor-specific pull mutation
 *   - Pull-result strip (ok/fail counts after a refresh)
 *   - Tab strip
 *   - Active-tab body
 *
 * Each vendor's page just declares its tabs (label, icon optional, body
 * component). Body components do their own useCapabilitySample reads.
 *
 * Mirrors the shape of PropertyAhrefs / PropertyDataForSEO but extracts
 * the boilerplate so adding a new vendor is ~80 lines, not 600.
 */
import { ReactNode, useState } from "react";
import { Card } from "@tremor/react";
import { RefreshCw, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UseMutationResult } from "@tanstack/react-query";
import type { VendorPullResponse } from "@/lib/queries";


export type LensTab = {
  key: string;
  label: string;
  body: ReactNode;
};


export function VendorLens({
  vendorIcon, vendorTitle, vendorDescription,
  pullCostNote, pullMutation, tabs, extraActions,
}: {
  vendorIcon: ReactNode;
  vendorTitle: string;
  vendorDescription: ReactNode;
  pullCostNote: string;          // shown in Refresh confirm
  pullMutation: UseMutationResult<VendorPullResponse, Error, { force?: boolean }>;
  tabs: LensTab[];
  /**
   * Optional extra actions rendered LEFT of the Refresh/Force buttons.
   * Used by per-vendor pages to surface the kind-implicit
   * <GenerateReportButton /> next to the data context it represents
   * (Traffic Report on PropertyGSC, Backlinks Report on PropertyBacklinks).
   */
  extraActions?: ReactNode;
}) {
  const [activeKey, setActiveKey] = useState<string>(tabs[0]?.key ?? "");

  const onRefresh = async () => {
    if (!confirm(
      `Pull every wired endpoint for this property? ${pullCostNote} `
      + `Cached results served if fresh (<24h).`
    )) return;
    try { await pullMutation.mutateAsync({ force: false }); } catch { /* surfaced inline */ }
  };
  const onForce = async () => {
    if (!confirm(`Force fresh pull (bypass cache)? ${pullCostNote}`)) return;
    try { await pullMutation.mutateAsync({ force: true }); } catch { /* surfaced inline */ }
  };

  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {vendorIcon}
            <h1 className="text-2xl font-semibold tracking-tight">{vendorTitle}</h1>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">{vendorDescription}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {extraActions}
          <button type="button" onClick={onRefresh} disabled={pullMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {pullMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
          <button type="button" onClick={onForce} disabled={pullMutation.isPending}
            title="Force fresh pull, bypass cache"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-xs hover:bg-accent disabled:opacity-50">
            Force
          </button>
        </div>
      </div>

      {pullMutation.error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          {pullMutation.error instanceof Error ? pullMutation.error.message : "Pull failed"}
        </div>
      )}
      {pullMutation.isSuccess && pullMutation.data && (
        <PullResultStrip data={pullMutation.data} />
      )}

      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        {tabs.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveKey(t.key)}
            className={cn(
              "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              activeKey === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}>
            {t.label}
          </button>
        ))}
      </div>

      <div>{active?.body}</div>
    </div>
  );
}


function PullResultStrip({ data }: { data: VendorPullResponse }) {
  const ok = data.results.filter((r) => r.ok).length;
  const failed = data.results.filter((r) => !r.ok);
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
      <div className="font-medium text-emerald-700 dark:text-emerald-400">
        Pulled {ok}/{data.results.length} endpoints in {data.duration_s.toFixed(1)}s.
      </div>
      {failed.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-amber-700 dark:text-amber-400">{failed.length} failed</summary>
          <ul className="mt-1 space-y-0.5 pl-3">
            {failed.map((r) => (
              <li key={r.endpoint} className="font-mono text-[10px]">
                <span className="text-muted-foreground">{r.endpoint}:</span>{" "}
                <span className="text-red-700 dark:text-red-400">{r.error}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


// ─── Reusable building blocks ──────────────────────────────────────


export type Column = {
  key: string;
  label: string;
  align?: "right";
  className?: string;
  render?: (row: Record<string, unknown>) => ReactNode;
};


export function DataTable({
  title, subtitle, columns, rows, sample, rowCount, icon,
}: {
  title: string;
  subtitle: string;
  columns: Column[];
  rows: Array<Record<string, unknown>>;
  sample: { fetched_at: string | null; available: boolean } | undefined;
  rowCount: number;
  icon?: ReactNode;
}) {
  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {icon}
            <h2 className="text-sm font-semibold">{title}</h2>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {sample?.available && sample.fetched_at && (
          <div className="shrink-0 text-right text-[11px] text-muted-foreground">
            <div>Cached {formatDateAgo(sample.fetched_at)}</div>
            <div>{rowCount.toLocaleString()} rows</div>
          </div>
        )}
      </div>

      {!sample?.available ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No data yet. Click <strong>Refresh</strong> at the top to pull.
        </div>
      ) : rowCount === 0 ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          The endpoint returned 0 rows.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="border-b border-border text-left">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={cn(
                    "px-2 py-1.5 font-medium text-muted-foreground",
                    c.align === "right" && "text-right",
                  )}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-accent/40">
                  {columns.map((c) => (
                    <td key={c.key} className={cn(
                      "px-2 py-1.5 align-top",
                      c.align === "right" && "text-right",
                      c.className,
                    )}>
                      {c.render ? c.render(r) : (r[c.key] != null ? String(r[c.key]) : "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


export function Headline({ label, value, sub }: {
  label: string;
  value: number | string | undefined | null;
  sub?: string;
}) {
  const display = value === undefined || value === null
    ? "no data"
    : (typeof value === "number" ? value.toLocaleString() : value);
  const isNoData = value === undefined || value === null;
  return (
    <Card>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-2xl font-semibold", isNoData && "text-muted-foreground italic")}>
        {display}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}


export function ExtLink({ href }: { href: string }) {
  if (!href) return <span>—</span>;
  return (
    <a href={href} target="_blank" rel="noreferrer"
       className="hover:underline" title={href}>
      {stripScheme(href)}
    </a>
  );
}


export function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}

export function formatDateAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}
